import {
  DIRECT_SALE_FONT_FACES,
  type DirectSaleFontFace,
} from '@designdeck/shared';

/**
 * The Direct Sale text panel's font grid, matching the design mock 1:1.
 *
 * Distinct from the Seller editor's FONTS list (config/fonts.ts): the mock
 * treats "Inter Bold" as its own grid entry, so an entry is a
 * family + weight PAIR — there is no separate bold toggle in Direct Sale.
 * The pairs come from the shared list the API commit and the server render
 * use too; only the grid labels live here. All five faces are self-hosted
 * via @fontsource (imported in main.tsx).
 */
export interface DirectSaleFont extends DirectSaleFontFace {
  /** Grid label shown to the customer (not translated — font names are proper nouns). */
  label: string;
}

/** Grid labels that differ from the family name. */
const GRID_LABELS: Record<string, string> = { Inter: 'Inter Bold' };

const withLabel = (face: DirectSaleFontFace): DirectSaleFont => ({
  label: GRID_LABELS[face.fontFamily] ?? face.fontFamily,
  fontFamily: face.fontFamily,
  fontWeight: face.fontWeight,
});

const [FIRST_FACE, ...OTHER_FACES] = DIRECT_SALE_FONT_FACES;

// A non-empty tuple, so the default below is a font and not `undefined`
export const DIRECT_SALE_FONTS: [DirectSaleFont, ...DirectSaleFont[]] = [
  withLabel(FIRST_FACE),
  ...OTHER_FACES.map(withLabel),
];

export const DEFAULT_DIRECT_SALE_FONT = DIRECT_SALE_FONTS[0];

/**
 * Resolve when the given face is usable on the canvas. Fabric measures
 * glyphs at render time, so applying a fontFamily before the face finished
 * loading renders fallback metrics — and those poisoned metrics end up in
 * the position previews handed to the shop. Single-face variant of
 * utils/canvasFonts (which covers every text on a canvas).
 *
 * Never rejects: a font that fails to load falls back visually, which is
 * preferable to blocking the customer's edit.
 */
export async function ensureFontLoaded(
  fontFamily: string,
  fontWeight: number
): Promise<void> {
  const face = `${fontWeight} 16px "${fontFamily}"`;
  try {
    if (document.fonts.check(face)) return;
    await document.fonts.load(face);
  } catch {
    // Face unavailable — the browser fallback renders instead.
  }
}
